import { useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { getUserList } from '@/apis/admin';
import type { CompanyInfo, UserFilterType } from '@/types/admin.types';

import { QUERY_KEY } from '@/constants/queryKey';
import { ROUTER } from '@/constants/constants';

import Text from '@/components/common/Text';
import { Theme } from '@/styles/Theme';
import styled from '@emotion/styled';

import * as S from './style';

import { phoneFormat } from '@/utils/convertNumberFormat';

const convertKRTime = (utcTime: string) => {
  const date = new Date(utcTime);
  const krTime = date.toLocaleString('ko-KR', {
    timeZone: 'Asia/Seoul',
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  return krTime;
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

const getSubscriptionStatusBadge = (status: CompanyInfo['subscription_status']) => {
  const statusMap = {
    active: { text: '유료', color: '#4CAF50' },
    expired: { text: '만료됨', color: '#F44336' },
    free: { text: '무료', color: '#666' },
  };
  return statusMap[status || 'free'] || statusMap.free;
};

const columnhelper = createColumnHelper<CompanyInfo>();

const createColumns = (onRowClick: (id: number) => void, startIndex: number = 0) => [
  columnhelper.accessor((row, index) => {
    // Calculate index based on pagination
    return startIndex + index + 1;
  }, {
    id: 'index',
    header: '번호',
    cell: (info) => info.getValue(),
    size: 60,
    enableSorting: false,
  }),
  columnhelper.accessor('company', {
    header: '회사명',
    cell: (info) => (
      <ClickableText onClick={() => onRowClick(info.row.original.id)}>
        {info.getValue()}
      </ClickableText>
    ),
    size: 180,
    enableSorting: true,
  }),
  columnhelper.accessor('email', {
    header: '이메일',
    cell: (info) => info.getValue(),
    size: 220,
    enableSorting: true,
  }),
  columnhelper.accessor('phone', {
    header: '연락처',
    cell: (info) => phoneFormat(info.getValue() || ''),
    size: 140,
    enableSorting: false,
  }),
  columnhelper.accessor('subscription_status', {
    header: '구독 상태',
    cell: (info) => {
      const badge = getSubscriptionStatusBadge(info.getValue());
      return (
        <S.StatusBadge color={badge.color}>
          {badge.text}
        </S.StatusBadge>
      );
    },
    size: 100,
    enableSorting: true,
  }),
  columnhelper.accessor('payment_method', {
    header: '결제 방식',
    cell: (info) => {
      const method = info.getValue();
      if (!method) return '-';
      return method === 'prepaid' ? '선불' : '후불';
    },
    size: 100,
    enableSorting: true,
  }),
  columnhelper.accessor('worker_count', {
    header: '작업자 수',
    cell: (info) => `${info.getValue() || 0}명`,
    size: 90,
    enableSorting: false,
  }),
  columnhelper.accessor('created_at', {
    header: '가입일',
    cell: (info) => formatDate(info.getValue()),
    size: 180,
    enableSorting: true,
  }),
  columnhelper.accessor('updated_at', {
    header: '최근 접속일',
    cell: (info) => convertKRTime(info.getValue()),
    size: 180,
    enableSorting: true,
  }),
];

// Map column IDs to database field names
const getSortField = (columnId: string): string | null => {
  const fieldMap: Record<string, string> = {
    company: 'company',
    email: 'email',
    subscription_status: 'subscription_status',
    payment_method: 'payment_method',
    created_at: 'created_at',
    updated_at: 'updated_at',
  };
  return fieldMap[columnId] || null;
};

interface UserTableProps {
  filters: UserFilterType & { page?: number; limit?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' };
  onSortChange?: (sortBy: string | undefined, sortOrder: 'asc' | 'desc' | undefined) => void;
}

const UserTable = ({ filters, onSortChange }: UserTableProps) => {
  const navigate = useNavigate();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: [QUERY_KEY.ADMIN.USER_LIST, filters],
    queryFn: () => {
      // Ensure page and limit are valid numbers
      const safeFilters = {
        ...filters,
        page: filters.page && !isNaN(filters.page) && filters.page > 0 ? filters.page : 1,
        limit: filters.limit && !isNaN(filters.limit) && filters.limit > 0 && filters.limit <= 100 ? filters.limit : 10,
      };
      return getUserList(safeFilters);
    },
  });

  const handleRowClick = (userId: number) => {
    navigate(`${ROUTER.ADMIN.USER_MANAGE}/${userId}`);
  };

  const handleSortChange = (columnId: string) => {
    if (!onSortChange) return;
    
    const sortField = getSortField(columnId);
    if (!sortField) return;

    const currentSortBy = filters.sortBy;
    const currentSortOrder = filters.sortOrder || 'desc';

    if (currentSortBy === sortField) {
      // Toggle sort order
      const newOrder = currentSortOrder === 'asc' ? 'desc' : 'asc';
      onSortChange(sortField, newOrder);
    } else {
      // New sort field
      onSortChange(sortField, 'desc');
    }
  };

  const page = filters.page || 1;
  const limit = filters.limit || 10;
  const startIndex = (page - 1) * limit;

  const memoizedColumns = useMemo(
    () => createColumns(handleRowClick, startIndex),
    [handleRowClick, startIndex]
  );

  const memoizedData = useMemo(
    () => data?.data?.data ?? [],
    [data]
  );

  const { getHeaderGroups, getRowModel } = useReactTable({
    columns: memoizedColumns,
    data: memoizedData,
    manualSorting: true, // Server-side sorting
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) {
    return <Text>로딩 중...</Text>;
  }

  if (isError) {
    return (
      <S.ErrorText>
        오류가 발생했습니다: {error instanceof Error ? error.message : '알 수 없는 오류'}
      </S.ErrorText>
    );
  }

  const total = data?.data?.total || 0;
  const start = total === 0 ? 0 : startIndex + 1;
  const end = Math.min(startIndex + limit, total);

  return (
    <S.TableContainer>
      <S.TableHeader>
        <Text fontSize='0.875rem' color={Theme.colors.G_1}>
          {start} - {end} / 총 {total}개
        </Text>
      </S.TableHeader>
      <S.Table>
        <thead>
          {getHeaderGroups().map((headerGroup) => (
            <S.Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const columnDef = header.column.columnDef;
                const isSortable = columnDef.enableSorting !== false;
                const sortField = getSortField(header.column.id);
                const isActiveSort = filters.sortBy === sortField;

                return (
                  <S.Th
                    key={header.id}
                    colSpan={header.colSpan}
                    style={{ width: header.getSize() }}
                    sortable={isSortable}
                    onClick={() => isSortable && sortField && handleSortChange(header.column.id)}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {isSortable && sortField && isActiveSort && (
                      <S.SortIndicator>
                        {filters.sortOrder === 'asc' ? '🔼' : '🔽'}
                      </S.SortIndicator>
                    )}
                  </S.Th>
                );
              })}
            </S.Tr>
          ))}
        </thead>
        <tbody>
          {getRowModel().rows.length === 0 ? (
            <S.Tr>
              <S.Td colSpan={memoizedColumns.length} style={{ textAlign: 'center' }}>
                데이터가 없습니다.
              </S.Td>
            </S.Tr>
          ) : (
            getRowModel().rows.map((row) => (
              <S.Tr
                key={row.id}
                onClick={() => handleRowClick(row.original.id)}
                style={{ cursor: 'pointer' }}
              >
                {row.getVisibleCells().map((cell) => (
                  <S.Td key={cell.id} style={{ width: cell.column.getSize() }}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </S.Td>
                ))}
              </S.Tr>
            ))
          )}
        </tbody>
      </S.Table>
    </S.TableContainer>
  );
};

const ClickableText = styled.span`
  color: ${Theme.colors.B_1};
  cursor: pointer;
  
  &:hover {
    color: ${Theme.colors.B_2};
  }
`;


export default UserTable;
